/**
 * stockMovements.ts — Datos históricos de movimientos de stock para modo demo.
 *
 * QUÉ HACE: Provee movimientos pre-poblados para los productos por defecto.
 * QUÉ EXPORTA: defaultStockMovements (StockMovimiento[])
 * QUÉ NO DEBE HACER: No importar componentes ni hooks React.
 */
import { StockMovimiento } from "@/types";

const daysAgo = (n: number, hours = 0): string =>
  new Date(Date.now() - n * 86_400_000 - hours * 3_600_000).toISOString();

/** Genera un ID de movimiento único para datos demo */
const mId = (suffix: string) => `mov_demo_${suffix}`;

export const defaultStockMovements: StockMovimiento[] = [
  // ══════════════════════════════════════════════════════
  // Agua con Gas Manantial 600ml — cat_beb_0 (Prioritario Demo)
  // ══════════════════════════════════════════════════════
  {
    id: mId("p0_01"),
    productoId: "cat_beb_0",
    tipo: "creacion",
    cantidad: 150,
    stockAntes: 0,
    stockDespues: 150,
    nota: "Stock inicial demo",
    usuarioNombre: "Juan Pérez",
    createdAt: daysAgo(30),
  },
  {
    id: mId("p0_v1"),
    productoId: "cat_beb_0",
    tipo: "venta_caja",
    cantidad: 1,
    stockAntes: 37,
    stockDespues: 36,
    nota: "Venta demo — Caja Bar",
    usuarioNombre: "Sofía Castro",
    cajaNombre: "Caja Bar",
    ventaId: "T-9901",
    createdAt: daysAgo(0, 14), // Hace 14 horas
  },
  {
    id: mId("p0_v2"),
    productoId: "cat_beb_0",
    tipo: "venta_caja",
    cantidad: 4,
    stockAntes: 26,
    stockDespues: 22,
    nota: "Venta demo — Caja principal",
    usuarioNombre: "Sofía Castro",
    cajaNombre: "Caja principal",
    ventaId: "T-9902",
    createdAt: daysAgo(0, 1), // Hace 1 hora
  },
  {
    id: mId("p0_v3"),
    productoId: "cat_beb_0",
    tipo: "venta_caja",
    cantidad: 1,
    stockAntes: 22,
    stockDespues: 21,
    nota: "Venta demo — Caja Bar",
    usuarioNombre: "Dylan Torres",
    cajaNombre: "Caja Bar",
    ventaId: "T-9903",
    createdAt: daysAgo(0, 0.5), // Hace 30 min
  },
  {
    id: mId("p0_02"),
    productoId: "cat_beb_0",
    tipo: "venta_tienda",
    cantidad: 5,
    stockAntes: 150,
    stockDespues: 145,
    nota: "Pedido tienda virtual #12",
    usuarioNombre: "Sistema (Tienda)",
    pedidoNumero: 12,
    createdAt: daysAgo(25),
  },
  {
    id: mId("p0_03"),
    productoId: "cat_beb_0",
    tipo: "credito",
    cantidad: 2,
    stockAntes: 145,
    stockDespues: 143,
    nota: "Venta credito a cliente frecuente",
    usuarioNombre: "Sofía Castro",
    cajaNombre: "Caja principal",
    creditoId: "fia_demo_1",
    createdAt: daysAgo(20),
  },
  {
    id: mId("p0_04"),
    productoId: "cat_beb_0",
    tipo: "reposicion",
    cantidad: 50,
    stockAntes: 143,
    stockDespues: 193,
    nota: "Lote nuevo de proveedor Manantial",
    usuarioNombre: "Juan Pérez",
    createdAt: daysAgo(15),
  },
  {
    id: mId("p0_05"),
    productoId: "cat_beb_0",
    tipo: "ajuste_manual",
    cantidad: 3,
    stockAntes: 193,
    stockDespues: 190,
    nota: "Ajuste: botellas con sello roto",
    usuarioNombre: "Juan Pérez",
    createdAt: daysAgo(10),
  },
  {
    id: mId("p0_06"),
    productoId: "cat_beb_0",
    tipo: "devolucion",
    cantidad: 1,
    stockAntes: 190,
    stockDespues: 191,
    nota: "Cliente cambió por Agua sin gas",
    usuarioNombre: "Sofía Castro",
    cajaNombre: "Caja principal",
    createdAt: daysAgo(8),
  },
  {
    id: mId("p0_07"),
    productoId: "cat_beb_0",
    tipo: "anulacion",
    cantidad: 5,
    stockAntes: 191,
    stockDespues: 196,
    nota: "Venta T-1050 anulada por error en mesa",
    usuarioNombre: "Juan Pérez",
    cajaNombre: "Caja Terraza",
    createdAt: daysAgo(5),
  },
  {
    id: mId("p0_08"),
    productoId: "cat_beb_0",
    tipo: "cancelacion_pedido",
    cantidad: 2,
    stockAntes: 196,
    stockDespues: 198,
    nota: "Pedido Tienda #15 cancelado por cliente",
    usuarioNombre: "Sistema (Tienda)",
    createdAt: daysAgo(3),
  },
  {
    id: mId("p0_09"),
    productoId: "cat_beb_0",
    tipo: "agotamiento",
    cantidad: 0,
    stockAntes: 198,
    stockDespues: 0,
    nota: "Stock marcado como AGOTADO manualmente",
    usuarioNombre: "Juan Pérez",
    createdAt: daysAgo(1),
  },

  // ══════════════════════════════════════════════════════
  // Agua Cristal 600ml — cat_beb_6
  // ══════════════════════════════════════════════════════
  {
    id: mId("p1_01"),
    productoId: "cat_beb_6",
    tipo: "creacion",
    cantidad: 200,
    stockAntes: 0,
    stockDespues: 200,
    nota: "Producto creado con stock inicial",
    usuarioNombre: "Juan Pérez",
    createdAt: daysAgo(60),
  },
  {
    id: mId("p1_02"),
    productoId: "cat_beb_6",
    tipo: "venta_caja",
    cantidad: 15,
    stockAntes: 200,
    stockDespues: 185,
    nota: "Venta en caja",
    usuarioNombre: "María López",
    cajaNombre: "Caja principal",
    ventaId: "T-10001",
    createdAt: daysAgo(55),
  },
  {
    id: mId("p1_03"),
    productoId: "cat_beb_6",
    tipo: "venta_caja",
    cantidad: 22,
    stockAntes: 185,
    stockDespues: 163,
    nota: "Venta en caja",
    usuarioNombre: "Juan Pérez",
    cajaNombre: "Caja Express",
    ventaId: "T-10045",
    createdAt: daysAgo(48),
  },
  {
    id: mId("p1_04"),
    productoId: "cat_beb_6",
    tipo: "venta_tienda",
    cantidad: 5,
    stockAntes: 163,
    stockDespues: 158,
    nota: "Pedido tienda virtual #3",
    usuarioNombre: "Sistema (Tienda)",
    pedidoNumero: 3,
    createdAt: daysAgo(44),
  },
  {
    id: mId("p1_05"),
    productoId: "cat_beb_6",
    tipo: "reposicion",
    cantidad: 42,
    stockAntes: 158,
    stockDespues: 200,
    nota: "Reposición de proveedor Postobón",
    usuarioNombre: "Juan Pérez",
    createdAt: daysAgo(40),
  },
  {
    id: mId("p1_06"),
    productoId: "cat_beb_6",
    tipo: "venta_caja",
    cantidad: 30,
    stockAntes: 200,
    stockDespues: 170,
    nota: "Venta en caja",
    usuarioNombre: "María López",
    ventaId: "T-10123",
    createdAt: daysAgo(30),
  },
  {
    id: mId("p1_07"),
    productoId: "cat_beb_6",
    tipo: "venta_caja",
    cantidad: 25,
    stockAntes: 170,
    stockDespues: 145,
    nota: "Venta en caja",
    usuarioNombre: "Juan Pérez",
    ventaId: "T-10234",
    createdAt: daysAgo(20),
  },
  {
    id: mId("p1_08"),
    productoId: "cat_beb_6",
    tipo: "venta_tienda",
    cantidad: 10,
    stockAntes: 145,
    stockDespues: 135,
    nota: "Pedido tienda virtual #7",
    usuarioNombre: "Sistema (Tienda)",
    pedidoNumero: 7,
    createdAt: daysAgo(15),
  },
  {
    id: mId("p1_09"),
    productoId: "cat_beb_6",
    tipo: "venta_caja",
    cantidad: 20,
    stockAntes: 135,
    stockDespues: 115,
    nota: "Venta en caja",
    usuarioNombre: "María López",
    ventaId: "T-10456",
    createdAt: daysAgo(10),
  },
  {
    id: mId("p1_10"),
    productoId: "cat_beb_6",
    tipo: "venta_caja",
    cantidad: 14,
    stockAntes: 115,
    stockDespues: 101,
    nota: "Venta en caja",
    usuarioNombre: "Juan Pérez",
    ventaId: "T-10789",
    createdAt: daysAgo(5),
  },

  // ══════════════════════════════════════════════════════
  // Coca-Cola 350ml — cat_beb_1
  // ══════════════════════════════════════════════════════
  {
    id: mId("p2_01"),
    productoId: "cat_beb_1",
    tipo: "creacion",
    cantidad: 150,
    stockAntes: 0,
    stockDespues: 150,
    nota: "Producto creado con stock inicial",
    usuarioNombre: "Juan Pérez",
    createdAt: daysAgo(90),
  },
  {
    id: mId("p2_02"),
    productoId: "cat_beb_1",
    tipo: "venta_caja",
    cantidad: 35,
    stockAntes: 150,
    stockDespues: 115,
    nota: "Venta en caja",
    usuarioNombre: "María López",
    ventaId: "T-20001",
    createdAt: daysAgo(75),
  },
  {
    id: mId("p2_03"),
    productoId: "cat_beb_1",
    tipo: "venta_caja",
    cantidad: 40,
    stockAntes: 115,
    stockDespues: 75,
    nota: "Venta en caja — semana alta demanda",
    usuarioNombre: "Juan Pérez",
    ventaId: "T-20056",
    createdAt: daysAgo(60),
  },
  {
    id: mId("p2_04"),
    productoId: "cat_beb_1",
    tipo: "venta_tienda",
    cantidad: 8,
    stockAntes: 75,
    stockDespues: 67,
    nota: "Pedido tienda virtual #2",
    usuarioNombre: "Sistema (Tienda)",
    pedidoNumero: 2,
    createdAt: daysAgo(50),
  },
  {
    id: mId("p2_05"),
    productoId: "cat_beb_1",
    tipo: "venta_caja",
    cantidad: 42,
    stockAntes: 67,
    stockDespues: 25,
    nota: "Venta en caja",
    usuarioNombre: "María López",
    ventaId: "T-20098",
    createdAt: daysAgo(35),
  },
  {
    id: mId("p2_06"),
    productoId: "cat_beb_1",
    tipo: "venta_caja",
    cantidad: 25,
    stockAntes: 25,
    stockDespues: 0,
    nota: "Venta en caja — producto agotado",
    usuarioNombre: "Juan Pérez",
    ventaId: "T-20145",
    createdAt: daysAgo(20),
  },
  {
    id: mId("p2_07"),
    productoId: "cat_beb_1",
    tipo: "agotamiento",
    cantidad: 0,
    stockAntes: 0,
    stockDespues: 0,
    nota: "Confirmado como agotado en sistema",
    usuarioNombre: "Juan Pérez",
    createdAt: daysAgo(18),
  },

  // ══════════════════════════════════════════════════════
  // Jugo Hit 200ml — cat_beb_3
  // ══════════════════════════════════════════════════════
  {
    id: mId("p3_01"),
    productoId: "cat_beb_3",
    tipo: "creacion",
    cantidad: 150,
    stockAntes: 0,
    stockDespues: 150,
    nota: "Producto creado con stock inicial",
    usuarioNombre: "Juan Pérez",
    createdAt: daysAgo(70),
  },
  {
    id: mId("p3_02"),
    productoId: "cat_beb_3",
    tipo: "venta_caja",
    cantidad: 28,
    stockAntes: 150,
    stockDespues: 122,
    nota: "Venta en caja",
    usuarioNombre: "María López",
    ventaId: "T-30001",
    createdAt: daysAgo(55),
  },
  {
    id: mId("p3_03"),
    productoId: "cat_beb_3",
    tipo: "venta_caja",
    cantidad: 20,
    stockAntes: 122,
    stockDespues: 102,
    nota: "Venta en caja",
    usuarioNombre: "Juan Pérez",
    ventaId: "T-30048",
    createdAt: daysAgo(40),
  },
  {
    id: mId("p3_04"),
    productoId: "cat_beb_3",
    tipo: "venta_tienda",
    cantidad: 12,
    stockAntes: 102,
    stockDespues: 90,
    nota: "Pedido tienda virtual #5",
    usuarioNombre: "Sistema (Tienda)",
    pedidoNumero: 5,
    createdAt: daysAgo(28),
  },
  {
    id: mId("p3_05"),
    productoId: "cat_beb_3",
    tipo: "venta_caja",
    cantidad: 18,
    stockAntes: 90,
    stockDespues: 72,
    nota: "Venta en caja",
    usuarioNombre: "María López",
    ventaId: "T-30099",
    createdAt: daysAgo(15),
  },
  {
    id: mId("p3_06"),
    productoId: "cat_beb_3",
    tipo: "ajuste_manual",
    cantidad: 10,
    stockAntes: 72,
    stockDespues: 62,
    nota: "Ajuste: unidades dañadas retiradas de bodega",
    usuarioNombre: "Juan Pérez",
    createdAt: daysAgo(8),
  },
  {
    id: mId("p3_07"),
    productoId: "cat_beb_3",
    tipo: "venta_caja",
    cantidad: 10,
    stockAntes: 62,
    stockDespues: 52,
    nota: "Venta en caja",
    usuarioNombre: "Juan Pérez",
    ventaId: "T-30156",
    createdAt: daysAgo(3),
  },

  // ══════════════════════════════════════════════════════
  // Pechuga de Pollo Fresca — cat_car_1 (ventaPorPeso)
  // ══════════════════════════════════════════════════════
  {
    id: mId("p5_01"),
    productoId: "cat_car_1",
    tipo: "reposicion",
    cantidad: 40,
    stockAntes: 0,
    stockDespues: 40,
    nota: "Entrada nueva: Frigorífico Nacional",
    usuarioNombre: "Juan Pérez",
    createdAt: daysAgo(15),
  },
  {
    id: mId("p5_02"),
    productoId: "cat_car_1",
    tipo: "venta_caja",
    cantidad: 3.5,
    stockAntes: 40,
    stockDespues: 36.5,
    nota: "Venta por peso en caja",
    usuarioNombre: "María López",
    ventaId: "T-50001",
    createdAt: daysAgo(14),
  },
  {
    id: mId("p5_03"),
    productoId: "cat_car_1",
    tipo: "venta_tienda",
    cantidad: 2,
    stockAntes: 36.5,
    stockDespues: 34.5,
    nota: "Pedido tienda virtual #8",
    usuarioNombre: "Sistema (Tienda)",
    pedidoNumero: 8,
    createdAt: daysAgo(12),
  },
  {
    id: mId("p5_04"),
    productoId: "cat_car_1",
    tipo: "venta_caja",
    cantidad: 5,
    stockAntes: 34.5,
    stockDespues: 29.5,
    nota: "Venta por peso en caja",
    usuarioNombre: "Juan Pérez",
    ventaId: "T-50023",
    createdAt: daysAgo(10),
  },
  {
    id: mId("p5_05"),
    productoId: "cat_car_1",
    tipo: "venta_caja",
    cantidad: 4.5,
    stockAntes: 29.5,
    stockDespues: 25,
    nota: "Venta por peso en caja",
    usuarioNombre: "María López",
    ventaId: "T-50067",
    createdAt: daysAgo(7),
  },
  {
    id: mId("p5_06"),
    productoId: "cat_car_1",
    tipo: "venta_caja",
    cantidad: 3,
    stockAntes: 25,
    stockDespues: 22,
    nota: "Venta por peso en caja",
    usuarioNombre: "Juan Pérez",
    ventaId: "T-50089",
    createdAt: daysAgo(4),
  },
  {
    id: mId("p5_07"),
    productoId: "cat_car_1",
    tipo: "venta_tienda",
    cantidad: 2,
    stockAntes: 22,
    stockDespues: 20,
    nota: "Pedido tienda virtual #11",
    usuarioNombre: "Sistema (Tienda)",
    pedidoNumero: 11,
    createdAt: daysAgo(2),
  },

  // ══════════════════════════════════════════════════════
  // Arroz Diana 500g — cat_gra_0
  // ══════════════════════════════════════════════════════
  {
    id: mId("p7_01"),
    productoId: "cat_gra_0",
    tipo: "creacion",
    cantidad: 100,
    stockAntes: 0,
    stockDespues: 100,
    nota: "Producto creado con stock inicial",
    usuarioNombre: "Juan Pérez",
    createdAt: daysAgo(80),
  },
  {
    id: mId("p7_02"),
    productoId: "cat_gra_0",
    tipo: "venta_caja",
    cantidad: 15,
    stockAntes: 100,
    stockDespues: 85,
    nota: "Venta en caja",
    usuarioNombre: "María López",
    ventaId: "T-70001",
    createdAt: daysAgo(60),
  },
  {
    id: mId("p7_03"),
    productoId: "cat_gra_0",
    tipo: "venta_caja",
    cantidad: 10,
    stockAntes: 85,
    stockDespues: 75,
    nota: "Venta en caja",
    usuarioNombre: "Juan Pérez",
    ventaId: "T-70034",
    createdAt: daysAgo(45),
  },
  {
    id: mId("p7_04"),
    productoId: "cat_gra_0",
    tipo: "venta_tienda",
    cantidad: 4,
    stockAntes: 75,
    stockDespues: 71,
    nota: "Pedido tienda virtual #4",
    usuarioNombre: "Sistema (Tienda)",
    pedidoNumero: 4,
    createdAt: daysAgo(33),
  },
  {
    id: mId("p7_05"),
    productoId: "cat_gra_0",
    tipo: "venta_caja",
    cantidad: 6,
    stockAntes: 71,
    stockDespues: 65,
    nota: "Venta en caja",
    usuarioNombre: "María López",
    ventaId: "T-70089",
    createdAt: daysAgo(20),
  },
  {
    id: mId("p7_06"),
    productoId: "cat_gra_0",
    tipo: "venta_caja",
    cantidad: 4,
    stockAntes: 65,
    stockDespues: 61,
    nota: "Venta en caja",
    usuarioNombre: "Juan Pérez",
    ventaId: "T-70145",
    createdAt: daysAgo(7),
  },

  // ══════════════════════════════════════════════════════
  // Frijol Cargamanto 500g — cat_gra_1
  // ══════════════════════════════════════════════════════
  {
    id: mId("p8_01"),
    productoId: "cat_gra_1",
    tipo: "creacion",
    cantidad: 80,
    stockAntes: 0,
    stockDespues: 80,
    nota: "Producto creado con stock inicial",
    usuarioNombre: "Juan Pérez",
    createdAt: daysAgo(75),
  },
  {
    id: mId("p8_02"),
    productoId: "cat_gra_1",
    tipo: "venta_caja",
    cantidad: 20,
    stockAntes: 80,
    stockDespues: 60,
    nota: "Venta en caja",
    usuarioNombre: "María López",
    ventaId: "T-80001",
    createdAt: daysAgo(55),
  },
  {
    id: mId("p8_03"),
    productoId: "cat_gra_1",
    tipo: "venta_caja",
    cantidad: 12,
    stockAntes: 60,
    stockDespues: 48,
    nota: "Venta en caja",
    usuarioNombre: "Juan Pérez",
    ventaId: "T-80056",
    createdAt: daysAgo(38),
  },
  {
    id: mId("p8_04"),
    productoId: "cat_gra_1",
    tipo: "venta_tienda",
    cantidad: 3,
    stockAntes: 48,
    stockDespues: 45,
    nota: "Pedido tienda virtual #6",
    usuarioNombre: "Sistema (Tienda)",
    pedidoNumero: 6,
    createdAt: daysAgo(25),
  },
  {
    id: mId("p8_05"),
    productoId: "cat_gra_1",
    tipo: "ajuste_manual",
    cantidad: 5,
    stockAntes: 45,
    stockDespues: 40,
    nota: "Ajuste: bolsas dañadas por humedad",
    usuarioNombre: "Juan Pérez",
    createdAt: daysAgo(15),
  },
  {
    id: mId("p8_06"),
    productoId: "cat_gra_1",
    tipo: "venta_caja",
    cantidad: 4,
    stockAntes: 40,
    stockDespues: 36,
    nota: "Venta en caja",
    usuarioNombre: "María López",
    ventaId: "T-80099",
    createdAt: daysAgo(5),
  },
];
